import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Android as AndroidIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  History as HistoryIcon,
  Publish as PublishIcon,
} from '@mui/icons-material';
import Layout from './Layout';
import { mobileVersionAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface MobileVersionResponse {
  id: number;
  version: string;
  buildNumber: number;
  apkUrl: string;
  apkSha256?: string | null;
  mandatory: boolean;
  changelog: string[];
  createdAt: string;
}

const formatDateTime = (value?: string) => {
  if (!value) return 'Unknown';

  try {
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  } catch (_error) {
    return value;
  }
};

const formatBytes = (bytes: number) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const power = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** power;
  return `${value.toFixed(power === 0 ? 0 : 2)} ${units[power]}`;
};

const isAdminUser = (roleId?: number | null, roleName?: string | null) =>
  roleId === 1 || String(roleName || '').toLowerCase() === 'admin';

const MobileVersionManager: React.FC = () => {
  const { user } = useAuth();
  const [version, setVersion] = useState('');
  const [buildNumber, setBuildNumber] = useState('');
  const [mandatory, setMandatory] = useState(false);
  const [changelogText, setChangelogText] = useState('');
  const [apkFile, setApkFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [latestVersion, setLatestVersion] = useState<MobileVersionResponse | null>(null);
  const [isLoadingLatest, setIsLoadingLatest] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const isAdmin = isAdminUser(user?.roleId, user?.roleName);

  const changelogItems = useMemo(
    () =>
      changelogText
        .split(/\r?\n/)
        .map((item) => item.trim())
        .filter(Boolean),
    [changelogText]
  );

  const fetchLatestVersion = async () => {
    try {
      setIsLoadingLatest(true);
      const response = await mobileVersionAPI.getLatest();
      setLatestVersion(response.data ?? null);
    } catch (_error) {
      setLatestVersion(null);
    } finally {
      setIsLoadingLatest(false);
    }
  };

  useEffect(() => {
    fetchLatestVersion();
  }, []);

  const resetForm = () => {
    setVersion('');
    setBuildNumber('');
    setMandatory(false);
    setChangelogText('');
    setApkFile(null);
    setFileInputKey((value) => value + 1);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setError(null);
    setSuccess(null);

    if (!file) {
      setApkFile(null);
      return;
    }

    if (!file.name.toLowerCase().endsWith('.apk')) {
      setApkFile(null);
      setError('Please select an .apk file.');
      event.target.value = '';
      return;
    }

    setApkFile(file);
  };

  const handleSubmit = async () => {
    setError(null);
    setSuccess(null);

    const trimmedVersion = version.trim();
    const parsedBuildNumber = Number.parseInt(buildNumber.trim(), 10);

    if (!trimmedVersion) {
      setError('Version is required.');
      return;
    }

    if (!Number.isInteger(parsedBuildNumber) || parsedBuildNumber < 0) {
      setError('Build number must be an integer greater than or equal to 0.');
      return;
    }

    if (!apkFile) {
      setError('APK file is required.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await mobileVersionAPI.create({
        version: trimmedVersion,
        buildNumber: parsedBuildNumber,
        mandatory,
        changelog: changelogItems,
        apk: apkFile,
      });

      setLatestVersion(response.data?.data ?? null);
      setSuccess(`APK ${trimmedVersion} uploaded successfully.`);
      resetForm();
    } catch (uploadError: any) {
      setError(uploadError?.response?.data?.message || 'Failed to upload APK.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Stack spacing={3}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 4,
              background: 'linear-gradient(135deg, #1f2937 0%, #334155 48%, #CA4300 100%)',
              color: '#fff',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                right: -40,
                top: -40,
                width: 180,
                height: 180,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.08)',
              }}
            />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Chip
                  icon={<AndroidIcon />}
                  label="Mobile Release Console"
                  sx={{
                    mb: 2,
                    color: '#fff',
                    bgcolor: 'rgba(255,255,255,0.12)',
                    '& .MuiChip-icon': { color: '#fff' },
                  }}
                />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  APK update upload
                </Typography>
                <Typography variant="body1" sx={{ maxWidth: 700, color: 'rgba(255,255,255,0.84)' }}>
                  Upload a new Android build, mark it as mandatory when needed, and keep the latest release details visible for the team.
                </Typography>
              </Box>

              {latestVersion ? (
                <Card
                  sx={{
                    minWidth: { xs: '100%', md: 320 },
                    borderRadius: 3,
                    bgcolor: 'rgba(255,255,255,0.92)',
                    color: '#111827',
                  }}
                >
                  <CardContent>
                    <Typography variant="overline" sx={{ color: '#CA4300', fontWeight: 700 }}>
                      Latest Uploaded
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {latestVersion.version}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Build #{latestVersion.buildNumber}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip
                        size="small"
                        label={latestVersion.mandatory ? 'Mandatory' : 'Optional'}
                        color={latestVersion.mandatory ? 'warning' : 'default'}
                      />
                      <Chip
                        size="small"
                        icon={<HistoryIcon />}
                        label={formatDateTime(latestVersion.createdAt)}
                        variant="outlined"
                      />
                    </Stack>
                  </CardContent>
                </Card>
              ) : null}
            </Stack>
          </Paper>

          {!isAdmin ? (
            <Alert severity="warning">
              Only admins can upload APK versions.
            </Alert>
          ) : null}

          {error ? <Alert severity="error">{error}</Alert> : null}
          {success ? <Alert severity="success">{success}</Alert> : null}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.1fr) minmax(320px, 0.9fr)' },
              gap: 3,
              alignItems: 'start',
            }}
          >
            <Card sx={{ borderRadius: 4, boxShadow: '0 16px 40px rgba(15, 23, 42, 0.08)' }}>
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                      Upload new build
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      The backend expects multipart form-data with fields `apk`, `version`, `buildNumber`, `mandatory`, and optional `changelog`.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                      gap: 2,
                    }}
                  >
                    <TextField
                      label="Version"
                      placeholder="1.2.3"
                      value={version}
                      onChange={(event) => setVersion(event.target.value)}
                      fullWidth
                      disabled={!isAdmin || isSubmitting}
                    />
                    <TextField
                      label="Build Number"
                      placeholder="123"
                      value={buildNumber}
                      onChange={(event) => setBuildNumber(event.target.value)}
                      fullWidth
                      disabled={!isAdmin || isSubmitting}
                    />
                  </Box>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={mandatory}
                        onChange={(event) => setMandatory(event.target.checked)}
                        disabled={!isAdmin || isSubmitting}
                      />
                    }
                    label="Mandatory update"
                  />

                  <TextField
                    label="Changelog"
                    placeholder={'One line per item\nBug fixes\nPerformance improvements'}
                    value={changelogText}
                    onChange={(event) => setChangelogText(event.target.value)}
                    minRows={5}
                    multiline
                    fullWidth
                    disabled={!isAdmin || isSubmitting}
                    helperText="Each non-empty line will be sent as a changelog item."
                  />

                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      borderStyle: 'dashed',
                      borderColor: apkFile ? '#CA4300' : 'divider',
                      bgcolor: apkFile ? 'rgba(202, 67, 0, 0.04)' : 'grey.50',
                    }}
                  >
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <CloudUploadIcon sx={{ color: '#CA4300' }} />
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            APK file
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Select the signed Android package to upload.
                          </Typography>
                        </Box>
                      </Stack>

                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<PublishIcon />}
                        disabled={!isAdmin || isSubmitting}
                        sx={{
                          alignSelf: 'flex-start',
                          borderColor: 'rgba(202, 67, 0, 0.4)',
                          color: '#CA4300',
                        }}
                      >
                        Choose APK
                        <input key={fileInputKey} hidden type="file" accept=".apk" onChange={handleFileChange} />
                      </Button>

                      {apkFile ? (
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', md: 'center' }}>
                          <Chip label={apkFile.name} color="warning" variant="outlined" />
                          <Typography variant="body2" color="text.secondary">
                            {formatBytes(apkFile.size)}
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No file selected yet.
                        </Typography>
                      )}
                    </Stack>
                  </Paper>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={!isAdmin || isSubmitting}
                      startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                      sx={{
                        bgcolor: '#CA4300',
                        px: 3,
                        '&:hover': { bgcolor: '#a83700' },
                      }}
                    >
                      {isSubmitting ? 'Uploading...' : 'Upload APK'}
                    </Button>
                    <Button
                      variant="text"
                      onClick={resetForm}
                      disabled={isSubmitting}
                    >
                      Reset form
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            <Stack spacing={3}>
              <Card sx={{ borderRadius: 4, boxShadow: '0 16px 40px rgba(15, 23, 42, 0.08)' }}>
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Stack spacing={2}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      Latest version
                    </Typography>

                    {isLoadingLatest ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : latestVersion ? (
                      <Stack spacing={2.5}>
                        <Box>
                          <Typography variant="h3" sx={{ fontWeight: 700, color: '#111827' }}>
                            {latestVersion.version}
                          </Typography>
                          <Typography variant="body1" color="text.secondary">
                            Build #{latestVersion.buildNumber}
                          </Typography>
                        </Box>

                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <Chip label={latestVersion.mandatory ? 'Mandatory update' : 'Optional update'} color={latestVersion.mandatory ? 'warning' : 'default'} />
                          <Chip label={formatDateTime(latestVersion.createdAt)} variant="outlined" />
                        </Stack>

                        <Divider />

                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            Changelog
                          </Typography>
                          {latestVersion.changelog.length > 0 ? (
                            <Stack spacing={1}>
                              {latestVersion.changelog.map((item, index) => (
                                <Typography key={`${item}-${index}`} variant="body2">
                                  {index + 1}. {item}
                                </Typography>
                              ))}
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No changelog provided.
                            </Typography>
                          )}
                        </Box>

                        {latestVersion.apkSha256 ? (
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                              SHA-256
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                wordBreak: 'break-all',
                                bgcolor: 'grey.100',
                                p: 1.5,
                                borderRadius: 2,
                              }}
                            >
                              {latestVersion.apkSha256}
                            </Typography>
                          </Box>
                        ) : null}

                        <Button
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          href={latestVersion.apkUrl}
                          target="_blank"
                          rel="noreferrer"
                          sx={{
                            alignSelf: 'flex-start',
                            borderColor: 'rgba(202, 67, 0, 0.35)',
                            color: '#CA4300',
                          }}
                        >
                          Download latest APK
                        </Button>
                      </Stack>
                    ) : (
                      <Alert severity="info">
                        No mobile version has been uploaded yet.
                      </Alert>
                    )}
                  </Stack>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 4, boxShadow: '0 16px 40px rgba(15, 23, 42, 0.08)' }}>
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Stack spacing={1.5}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Upload rules
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Only `.apk` files are accepted by the backend.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Maximum upload size is 2 GB.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      `buildNumber` must be an integer greater than or equal to `0`.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Changelog lines are sent as a JSON array, matching the backend parser.
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Box>
        </Stack>
      </Container>
    </Layout>
  );
};

export default MobileVersionManager;
